import { motion } from "framer-motion";
import { Lightbulb, Paintbrush, ShieldCheck, Heart } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const icons = [Lightbulb, Paintbrush, ShieldCheck, Heart];

const WhyMe = () => {
  const { t } = useLanguage();

  return (
    <section
      id="miert-engem"
      data-testid="why-me-section"
      className="relative py-24 md:py-32 bg-[#F8FAFC]"
    >
      {/* Subtle decorative elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 -left-40 w-[28rem] h-[28rem] rounded-full bg-[#3B82F6] opacity-[0.04] blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="max-w-3xl mb-16 md:mb-20"
        >
          <span className="inline-block text-xs md:text-sm font-medium uppercase tracking-[0.25em] text-[#3B82F6] mb-6">
            {t.whyMe.label}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1B2A4A] leading-[1.1] tracking-tight">
            {t.whyMe.headingStart}{" "}
            <span className="italic font-medium">{t.whyMe.headingItalic}</span>
            {t.whyMe.headingEnd}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {t.whyMe.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                data-testid={`why-me-item-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.08,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                className="group flex gap-6 p-8 md:p-10 bg-white rounded-2xl transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_-18px_rgba(27,42,74,0.18)]"
              >
                <div className="flex-shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F8FAFC] shadow-sm transition-all duration-500 group-hover:bg-[#3B82F6] group-hover:scale-110">
                  <Icon
                    strokeWidth={1.5}
                    className="w-6 h-6 text-[#3B82F6] transition-colors duration-500 group-hover:text-white"
                  />
                </div>
                <div>
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-[#1B2A4A] leading-snug mb-3">
                    {item.title}
                  </h3>
                  <p className="text-base text-[#475569] leading-relaxed font-light">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyMe;
